'use client';

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsent } from '@/providers/consent-provider';
import { LocaleLink } from '@/components/locale-link';
import { Icons } from '@/components/common/icons';

export function Footer() {
  const { t } = useTranslation();
  const { showConsentModal } = useConsent();
  const currentYear = new Date().getFullYear();
  const footerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const updateFooterHeight = () => {
      const height = footerRef.current?.offsetHeight;
      if (!height) return;
      document.body.style.setProperty('--footer-height', `${height}px`);
    };

    updateFooterHeight();

    const observer = new ResizeObserver(() => {
      updateFooterHeight();
    });

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }
    window.addEventListener('resize', updateFooterHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateFooterHeight);
    };
  }, []);

  return (
    <footer
      ref={footerRef}
      className="footer border-t bg-background/50 backdrop-blur-sm w-full max-w-full overflow-x-hidden"
      data-nosnippet=""
    >
      <div className="flex flex-col md:flex-row justify-center md:justify-between items-center gap-3 py-2 px-6 w-full">
        <div className="flex order-2 md:order-1 items-center gap-2 font-normal text-xs text-muted-foreground">
          <span>{currentYear} &copy;</span>
          <span className="font-medium text-foreground/80">Firmendata UG (haftungsbeschränkt)</span>
          <a
            href="https://www.linkedin.com/company/firmendata/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
            className="inline-flex items-center text-[#0A66C2] hover:text-[#004182] transition-colors"
          >
            <Icons.linkedin className="size-4" />
          </a>
        </div>
        <nav className="flex flex-wrap justify-center order-1 md:order-2 gap-4 font-normal text-xs text-muted-foreground">
          <LocaleLink
            href="/contact"
            className="hover:text-primary transition-colors"
          >
            {t('navigation.contact')}
          </LocaleLink>
          <LocaleLink
            href="/faq"
            className="hover:text-primary transition-colors"
          >
            {t('navigation.faq')}
          </LocaleLink>
          <a
            href="https://status.firmendata.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary transition-colors"
          >
            {t('navigation.status')}
          </a>
          <LocaleLink
            href="/impressum"
            className="hover:text-primary transition-colors"
          >
            {t('navigation.imprint')}
          </LocaleLink>
          <LocaleLink
            href="/datenqualitaet"
            className="hover:text-primary transition-colors"
          >
            {t('navigation.dataQuality')}
          </LocaleLink>
          <LocaleLink
            href="/sources"
            className="hover:text-primary transition-colors"
          >
            {t('navigation.sources')}
          </LocaleLink>
          <LocaleLink
            href="/privacy-policy"
            className="hover:text-primary transition-colors"
          >
            {t('navigation.privacyPolicy')}
          </LocaleLink>
          <LocaleLink
            href="/neugruendungen"
            className="hover:text-primary transition-colors"
          >
            {t('navigation.newCompanies')}
          </LocaleLink>
          <LocaleLink
            href="/listen"
            className="hover:text-primary transition-colors"
          >
            {t('navigation.topLists')}
          </LocaleLink>
          <LocaleLink
            href="/agb"
            className="hover:text-primary transition-colors"
          >
            {t('navigation.agb')}
          </LocaleLink>
          <LocaleLink
            href="/legal/dpa"
            className="hover:text-primary transition-colors"
          >
            {t('navigation.dpa')}
          </LocaleLink>
          <button
            type="button"
            onClick={showConsentModal}
            className="hover:text-primary transition-colors"
          >
            {t('navigation.cookieSettings')}
          </button>
        </nav>
      </div>
    </footer>
  );
}
